import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { faltaAtendimentoById } from '../../../../atendimento/core/_requests'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import {
  Button,
  FormSelect,
  FormTextArea,
  IOption,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import {
  ButtonType,
  MotivoCancelamentoAtendimentoType,
  OptionsHelper,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { marcarFaltaSchema } from './validation/marcarFaltaSchema'

interface MarcarFaltaModalProps {
  atendimentoId: number
  /** Chamado depois de registrar a falta com sucesso (ex.: encerrar a chamada ativa — LO-829). */
  onAtendimentoEncerrado?: () => void
}

const MarcarFaltaModal: FC<MarcarFaltaModalProps> = ({ atendimentoId, onAtendimentoEncerrado }) => {
  const { closeModal } = useModal()
  const { refetch } = useQueryResponse()
  const { t } = useT()
  const navigate = useNavigate()

  const initialValues = {
    motivo: undefined as IOption<string> | undefined,
    motivoDetalhado: '',
  }

  const formik = useFormik({
    initialValues,
    validationSchema: useMemo(() => marcarFaltaSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        // Código e detalhamento vão em campos separados: o servidor deriva a classe do motivo
        // e recusa código desconhecido, motivo de sistema e "Outros" sem detalhamento.
        const motivoCodigo = values.motivo?.value as string
        const motivoDetalhe =
          motivoCodigo === MotivoCancelamentoAtendimentoType.Outros
            ? values.motivoDetalhado.trim()
            : undefined

        // A chamada é aguardada: sem o await, a recusa do servidor não chegaria ao catch e o
        // profissional veria confirmação de sucesso com a falta não registrada.
        await faltaAtendimentoById(atendimentoId, motivoCodigo, motivoDetalhe)
        refetch()
        await showAlert({
          title: t('common.success'),
          text: 'Atendimento alterado com sucesso',
          icon: 'success',
        })
        closeModal()
        onAtendimentoEncerrado?.()
        navigate('/apps/atendimentosMedicos/lista')
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const isOutros = formik.values.motivo?.value === MotivoCancelamentoAtendimentoType.Outros

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <FormSelect
        formik={formik}
        name='motivo'
        entity='motivo_falta'
        fetchOptions={OptionsHelper.getOptionsMotivosNaoRealizacao}
        isDisabled={formik.isSubmitting}
        isRequired
      />

      {isOutros && (
        <FormTextArea
          formik={formik}
          name='motivoDetalhado'
          entity='detalhes_falta'
          className='mt-3'
          isDisabled={formik.isSubmitting}
          rows={3}
          isRequired
        />
      )}

      <div className='d-flex justify-content-end mt-6'>
        <Button
          type={ButtonType.Reset}
          onClick={closeModal}
          className='btn-secondary me-3'
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.confirm')}
        </Button>
      </div>
    </form>
  )
}

export { MarcarFaltaModal }
